import { NotificationService, useFormatAmount, useIntl } from '@capsicum/common';
import { ModalService, Notification, useRouter } from '@capsicum/common-web';
import { CircleMarketplaceIcon, FtoIcon, VoucherIcon } from '@capsicum/common-web/icons';
import {
	useCancelOrderMutation,
	useLedgerSummaryNonTradableDetails,
	useWalletNotificationAtom
} from '@capsicum/features/wallet/shared';
import styled, { useTheme } from 'styled-components';

import { CancelOrderDrawer } from '../../../ui';
import { CardSectionSkeleton } from '../WalletPartnerDetails.skeletons';
import { SectionCardListWrapper, SectionTitle, SectionWrapper } from '../WalletPartnerDetails.styled';

type OnHoldSectionProps = {
	ledgerId: string;
	partnerCode?: string;
};

enum OnHoldIcons {
	FTO = 'fto',
	Marketplace = 'marketplace',
	Voucher = 'voucher'
}

export const OnHoldSection = ({ ledgerId, partnerCode }: OnHoldSectionProps) => {
	const { $t } = useIntl();
	const { colors } = useTheme();
	const { formatAmount } = useFormatAmount();

	const { isLoading, nonTradableDetails, refetch } = useLedgerSummaryNonTradableDetails(ledgerId);
	const { isPending: isCancelOrderLoading, mutate: cancelOrder } = useCancelOrderMutation();
	const { setWalletNotification } = useWalletNotificationAtom();
	const { push } = useRouter();

	const sectionTitle = $t('wallet.partnerWallet.onHold.title');

	const getItemIcon = (type: OnHoldIcons) => {
		switch (type) {
			case OnHoldIcons.Marketplace:
				return <CircleMarketplaceIcon color={colors.icons.main.enabled} height={48} width={48} />;
			case OnHoldIcons.Voucher:
				return <VoucherIcon color={colors.icons.main.enabled} height={48} width={48} />;
			case OnHoldIcons.FTO:
				return <FtoIcon color={colors.icons.main.enabled} height={48} width={48} />;
			default:
				return <CircleMarketplaceIcon color={colors.icons.main.enabled} height={48} width={48} />;
		}
	};

	const handleSuccessCancelOrder = () => {
		refetch();
		push('/wallet');
		setWalletNotification({
			description: $t('wallet.partnerWallet.onHold.cancelSuccessDesc'),
			title: $t('wallet.partnerWallet.onHold.cancelSuccessTitle'),
			type: 'success'
		});
	};

	const handleErrorCancelOrder = () => {
		NotificationService.addNotification({
			component: (
				<Notification
					message={$t('wallet.partnerWallet.onHold.cancelErrorDesc')}
					title={$t('wallet.partnerWallet.onHold.cancelErrorTitle')}
					variant="error"
				/>
			)
		});
	};

	const handleClickCancelOrder = (orderId?: string) => {
		ModalService.addModal({
			renderComponent: ({ close }) => (
				<CancelOrderDrawer
					cancelOrder={() =>
						cancelOrder(orderId || '', {
							onError: handleErrorCancelOrder,
							onSuccess: handleSuccessCancelOrder
						})
					}
					closeModal={close}
					testID="partnerWallet-onHold-cancelOrderDrawer"
				/>
			)
		});
	};

	if (isLoading) {
		return <CardSectionSkeleton />;
	}

	if (nonTradableDetails && nonTradableDetails?.length === 0) {
		return null;
	}

	return (
		<SectionWrapper>
			<SectionTitle>{sectionTitle}</SectionTitle>
			<SectionCardListWrapper>
				{nonTradableDetails?.map((item, index) => (
					<S.CardWrapper key={index + item.metadata?.orderId}>
						<S.CardStart>{getItemIcon(item.icon as OnHoldIcons)}</S.CardStart>
						<S.CardContent>
							<S.CardTitle>{item.title}</S.CardTitle>
							<S.CardDescription>{item.category}</S.CardDescription>
							<S.CardStatus>{item.subtitle}</S.CardStatus>
							{item.metadata?.isCancellableOrder && !isCancelOrderLoading && (
								<S.CancelOrder onClick={() => handleClickCancelOrder(item.metadata?.orderId)}>
									{$t('wallet.partnerWallet.onHold.cancelOrder')}
								</S.CancelOrder>
							)}
						</S.CardContent>
						<S.CardEnd>
							{formatAmount(item?.amount)}
							{partnerCode!}
						</S.CardEnd>
					</S.CardWrapper>
				))}
			</SectionCardListWrapper>
		</SectionWrapper>
	);
};

namespace S {
	export const NoItems = styled.span`
		${({ theme }) => theme.fonts.text.text3}
	`;

	export const CardWrapper = styled.div`
		display: flex;
		align-items: center;
		width: 100%;
		background-color: ${({ theme }) => theme.colors.backgrounds.main.container};
		padding: ${({ theme }) => theme.spacings.small} 0;
		${({ theme }) => theme.border.radius.medium};
	`;

	export const CardStart = styled.div`
		display: flex;
		order: 1;
		padding: 0 ${({ theme }) => theme.spacings.small};
	`;

	export const CardContent = styled.div`
		display: flex;
		flex: 1;
		flex-direction: column;
		order: 2;
	`;

	export const CardEnd = styled.div`
		${({ theme }) => theme.fonts.headings.title6}
		text-align: right;
		display: flex;
		order: 3;
		padding: 0 ${({ theme }) => theme.spacings.small};
	`;

	export const CardTitle = styled.span`
		${({ theme }) => theme.fonts.text.caption2}
		margin-bottom: ${({ theme }) => theme.spacings.xxxsmall};
	`;
	export const CardDescription = styled.span`
		${({ theme }) => theme.fonts.text.text1}
		margin-bottom: ${({ theme }) => theme.spacings.xxsmall};
	`;
	export const CardStatus = styled.span`
		${({ theme }) => theme.fonts.text.caption1}
	`;
	export const CancelOrder = styled.div`
		${({ theme }) => theme.fonts.text.caption1}
		color: ${({ theme }) => theme.colors.white};
		background-color: ${({ theme }) => theme.colors.backgrounds.brand.enabled};
		padding: 6px ${({ theme }) => theme.spacings.small};
		border-radius: 30px;
		width: fit-content;
		margin-top: ${({ theme }) => theme.spacings.xsmall};
		cursor: pointer;
	`;
}
